// Shop.jsx
import { useState, useContext, useRef, useEffect } from 'react';
import {
    Typography,
    Button,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Card,
    CardContent,
    Grid,
    Paper,
    Snackbar,
    Alert,
} from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { getAuth } from 'firebase/auth';
// NEW: Firebase storage for preloading card images
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import { UserContext } from '../contexts/UserContext';
import { PlayingCard } from '../components/PlayingCard';
import cardBack from '../data/card-back.jpg';
import goldPile from '../data/goldPile.jpg';
import soulDust from '../data/soulDust.jpg';

// ---------------- THEME (Beasts of Legend) ----------------
const THEME = {
    bg: '#0a0a0a',
    panel: 'rgba(0,0,0,0.72)',
    text: '#f9f6e3',
    textDim: 'rgba(249,246,227,0.75)',
    gold: '#d4af37',
    goldGlow: 'rgba(212,175,55,0.75)',
    goldDim: 'rgba(212,175,55,0.6)',
    borderGold: 'rgba(212,175,55,0.35)',
    shadowGold: '0 0 20px rgba(212,175,55,0.18), inset 0 0 12px rgba(212,175,55,0.12)',
};

// ⭐ PACK CONFIG
const PACKS = [
    { id: 'basic-single', label: 'Draw 1 (100 coins)', type: 'single', cost: 100, isSingle: true },

    { id: 'random-10', label: 'Random Pack (10 for 1000)', type: 'random', cost: 1000, isSingle: false },
    { id: 'man-10', label: 'Man Pack (10 for 1000)', type: 'man', cost: 1000, isSingle: false },
    { id: 'beast-10', label: 'Beast Pack (10 for 1000)', type: 'beast', cost: 1000, isSingle: false },

    { id: 'rarePlus-single', label: 'Rare+ Single Draw', type: 'rarePlus', cost: 0, isSingle: true, ticketType: 'rarePlus', ticketsRequired: 1 },
    { id: 'rarePlus-10', label: 'Rare+ Ticket Pack (10)', type: 'rarePlus', cost: 0, isSingle: false, ticketType: 'rarePlus', ticketsRequired: 10 },

    { id: 'highRarePlus-single', label: 'High Rare+ Single Draw', type: 'highRarePlus', cost: 0, isSingle: true, ticketType: 'highRarePlus', ticketsRequired: 1 },
    { id: 'highRarePlus-10', label: 'High Rare+ Ticket Pack (10)', type: 'highRarePlus', cost: 0, isSingle: false, ticketType: 'highRarePlus', ticketsRequired: 10 },

    { id: 'superRarePlus-single', label: 'Super Rare+ Single Draw', type: 'superRarePlus', cost: 0, isSingle: true, ticketType: 'superRarePlus', ticketsRequired: 1 },
    { id: 'superRarePlus-10', label: 'Super Rare+ Ticket Pack (10)', type: 'superRarePlus', cost: 0, isSingle: false, ticketType: 'superRarePlus', ticketsRequired: 10 },
];

// Ticket label mapping for button text
const TICKET_LABELS = {
    rarePlus: 'Rare Plus Ticket',
    highRarePlus: 'High Rare Plus Ticket',
    superRarePlus: 'Super Rare Plus Ticket',
};

const labelForTicket = (ticketType) => TICKET_LABELS[ticketType] || 'Ticket';

// ---------------- Animations ----------------
const flip = keyframes`
  0%   { transform: rotateY(90deg); opacity: 0; }
  100% { transform: rotateY(0);     opacity: 1; }
`;

const pulse = keyframes`
  0%   { box-shadow: 0 0 10px 0 ${THEME.borderGold}, 0 0 24px 0 var(--glow); }
  50%  { box-shadow: 0 0 20px 2px ${THEME.borderGold}, 0 0 42px 6px var(--glow); }
  100% { box-shadow: 0 0 10px 0 ${THEME.borderGold}, 0 0 24px 0 var(--glow); }
`;

const FlipCard = styled(Card)`
  animation: ${flip} 0.5s ease forwards;
  transform-style: preserve-3d;
  background: ${THEME.panel};
  border-radius: 16px;
  border: 1px solid ${THEME.borderGold};
`;

// Back face (card back art)
const backFaceStyles = {
    width: '100%',
    maxWidth: 420,
    height: 'auto',
    aspectRatio: '3 / 4',
    borderRadius: 14,
    backgroundImage: `url(${cardBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: `1px solid ${THEME.borderGold}`,
    boxShadow: THEME.shadowGold,
};

// Soul Dust front — EXACT same sizing/border as card back, just different image
const soulDustFrontStyles = {
    ...backFaceStyles,
    backgroundImage: `url(${soulDust})`,
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    overflow: 'hidden',
};

// Coin store tiles use gold pile as bg
const coinBoxStyle = {
    height: 280,
    borderRadius: 14,
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    background: `${THEME.panel}`,
    border: `1px solid ${THEME.borderGold}`,
    boxShadow: THEME.shadowGold,
    '::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.2)), url(${goldPile})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'saturate(1.1)',
        zIndex: 0,
    },
    '& > *': { zIndex: 1 },
};

// Soul Dust tiles—reuse style, tweak overlay
const soulBoxStyle = {
    ...coinBoxStyle,
    '::before': {
        ...coinBoxStyle['::before'],
        backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.2)), url(${soulDust})`,
    },
};

const pillStyle = {
    background: 'linear-gradient(180deg, rgba(212,175,55,0.25), rgba(212,175,55,0.05))',
    color: THEME.text,
    border: `1px solid ${THEME.goldDim}`,
    borderRadius: '999px',
    width: '200px',
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.15rem',
    mb: 2,
    fontFamily: "'Cinzel', serif",
    letterSpacing: '0.6px',
    boxShadow: THEME.shadowGold,
};

const goldButton = {
    background: 'linear-gradient(180deg, #e3c770, #c49b1f)',
    color: '#1a1a1a',
    border: '1px solid #b68c19',
    fontWeight: 700,
    fontFamily: "'Cinzel', serif",
    letterSpacing: '0.6px',
    textShadow: 'none',
    boxShadow: '0 0 12px rgba(212,175,55,0.35)',
    '&:hover': {
        background: 'linear-gradient(180deg, #f0d988, #cfa12a)',
        boxShadow: '0 0 18px rgba(212,175,55,0.55)',
    },
    '&:disabled': {
        filter: 'grayscale(0.3)',
        opacity: 0.65,
    },
};

// ---- Rarity glow helpers ----
export const rarityColor = (rarityRaw) => {
    const r = String(rarityRaw || '').toLowerCase();
    if (r === 'common') return '#9de19d';
    if (r === 'uncommon') return '#2e7d32';
    if (r === 'rare') return '#90caf9';
    if (r === 'high rare') return '#0d47a1';
    if (r === 'super rare') return '#ce93d8';
    if (r === 'super super rare') return '#4a148c';
    if (r === 'legendary') return '#FFD700';
    return '#cccccc';
};

const classificationLabel = (cls) => {
    const c = String(cls || '').toLowerCase();
    if (c === 'man') return 'Man';
    if (c === 'beast') return 'Beast';
    return cls || '—';
};

// ⭐ CoinBox: include `id` for packId
const CoinBox = ({ id, coins, price, onBuy }) => (
    <>
        <Box sx={pillStyle}>{coins.toLocaleString()} Coins</Box>
        <Button
            variant="contained"
            size="large"
            onClick={() => onBuy({ id, coins, price })}
            sx={{ width: 160, py: 1.2, ...goldButton }}
        >
            ${price.toFixed(2)}
        </Button>
    </>
);

// Soul Dust purchase box (costs coins)
const SoulBox = ({ soul, costCoins, onBuy, disabled }) => (
    <>
        <Box sx={pillStyle}>{soul.toLocaleString()} Soul Dust</Box>
        <Button
            variant="contained"
            size="large"
            disabled={disabled}
            onClick={() => onBuy({ soul, costCoins })}
            sx={{ width: 180, py: 1.2, ...goldButton }}
        >
            {costCoins.toLocaleString()} Coins
        </Button>
    </>
);

// ---------- Backend URLs ----------
const CF_BASES = {
    drawPack: 'https://drawpack-qfihkipuza-uc.a.run.app',
    drawCard: 'https://drawcard-qfihkipuza-uc.a.run.app',
    coinCheckout: 'https://createcoincheckout-qfihkipuza-uc.a.run.app',
};

// Small helper to retry on transient 429s with jitter
async function fetchWithRetry(url, options = {}, retries = 2) {
    let attempt = 0;
    let lastErr;
    while (attempt <= retries) {
        try {
            const res = await fetch(url, { ...options, credentials: 'omit' });
            if (!res.ok) {
                let body = '';
                try {
                    body = await res.text();
                } catch { }
                const err = new Error(`HTTP ${res.status}${body ? ` – ${body}` : ''}`);
                err.status = res.status;
                throw err;
            }
            return res;
        } catch (e) {
            lastErr = e;
            const status = e?.status;
            if (status === 429 || status === 503 || status === 504) {
                const backoff = 250 + Math.random() * 400 * (attempt + 1);
                await new Promise((r) => setTimeout(r, backoff));
                attempt += 1;
                continue;
            }
            break;
        }
    }
    throw lastErr;
}

// ---------- Card image resolution + preloading (Firebase) ----------

// Local cache just for this file; PlayingCard has its own cache for its own lookups.
const imageUrlCacheShop = new Map();

const evoClamp = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 1;
    return Math.max(1, Math.min(3, x));
};

const buildCanonicalPath = (cardId, evolutionStage) => {
    const evo = evoClamp(evolutionStage);
    return `cards/card-images/${cardId}_evo${evo}.png`;
};

const candidatePathsForShop = ({ ownedCard, cardDoc }) => {
    const evo = evoClamp(ownedCard?.evolutionStage || cardDoc?.evolutionStage || 1);
    const cardId = ownedCard?.cardId || cardDoc?.cardId || cardDoc?.id;

    const arr = [];
    if (cardDoc && cardDoc.images && cardDoc.images[`evo${evo}`]) {
        arr.push(cardDoc.images[`evo${evo}`]);
    }
    if (cardDoc && typeof cardDoc.image === 'string' && cardDoc.image) {
        arr.push(cardDoc.image);
    }
    if (cardId) {
        arr.push(buildCanonicalPath(cardId, evo));
    }
    const dedup = [...new Set(arr)];
    if (cardId) dedup.push(buildCanonicalPath(cardId, 1)); // fallback evo1
    return { list: dedup, cardId };
};

const getUrlForStoragePathShop = async (path) => {
    if (imageUrlCacheShop.has(path)) return imageUrlCacheShop.get(path);
    const storage = getStorage();
    const url = await getDownloadURL(ref(storage, path));
    imageUrlCacheShop.set(path, url);
    return url;
};

const resolveCardImageUrlForShop = async (ownedCard, cardDoc) => {
    const { list, cardId } = candidatePathsForShop({ ownedCard, cardDoc });
    if (!cardId) throw new Error('Missing cardId for image resolution (shop).');

    let lastErr = null;
    for (const p of list) {
        try {
            const url = await getUrlForStoragePathShop(p);
            return url;
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr || new Error('Unable to resolve image URL (shop).');
};

const preloadImageTag = (src) =>
    new Promise((resolve) => {
        if (!src || typeof Image === 'undefined') return resolve();
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });

const getCardImageKey = (card) => {
    if (!card?.cardId) return null;
    const evo = evoClamp(card?.evolutionStage || 1);
    return `${card.cardId}-evo${evo}`;
};

const preloadArtForCards = async (cards, globalCards, setPreloadedImages) => {
    if (!Array.isArray(cards) || !cards.length) return;

    const entries = await Promise.all(
        cards.map(async (card) => {
            const key = getCardImageKey(card);
            if (!key) return null;

            try {
                const instance = card || null;
                const base =
                    (card?.cardId && globalCards?.[card.cardId])
                        ? globalCards[card.cardId]
                        : card || null;

                if (!instance || !base) return null;

                const url = await resolveCardImageUrlForShop(instance, base);
                await preloadImageTag(url);
                return [key, url];
            } catch (e) {
                console.warn('Failed to preload art for card', card?.cardId, e);
                return null;
            }
        })
    );

    const updates = {};
    for (const pair of entries) {
        if (!pair) continue;
        const [key, url] = pair;
        updates[key] = url;
    }

    if (Object.keys(updates).length) {
        setPreloadedImages((prev) => ({ ...prev, ...updates }));
    }
};

function Shop() {
    const {
        userData,
        setUserData,
        persistUserData,
        addCardsToOwned,
        spendCoins,
        addCoins, // still available if needed later
        consumeBasicTicket,
        hasBasicTicket,
        addSoulDust,
        globalCards, // 🔥 pull globalCards so we can resolve art here
    } = useContext(UserContext);

    const [loading, setLoading] = useState(false);
    const [drawQueue, setDrawQueue] = useState([]);
    const [currentDraw, setCurrentDraw] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [finalDrawnCards, setFinalDrawnCards] = useState([]);

    const [flipPhase, setFlipPhase] = useState('back');
    const [flipKey, setFlipKey] = useState(0);

    const [selectedOption, setSelectedOption] = useState(null);
    const [buyDialogOpen, setBuyDialogOpen] = useState(false);

    // Snackbar for payment status
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // 🔥 Map from "cardId-evoX" -> preloaded https image URL
    const [preloadedImages, setPreloadedImages] = useState({});

    // Prevent double-invocation just in case
    const inFlightRef = useRef(false);

    const handleClickBuy = (option) => {
        setSelectedOption(option);
        setBuyDialogOpen(true);
    };

    const handleConfirm = async () => {
        if (!selectedOption) return;

        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
                alert('You must be logged in to purchase coins.');
                return;
            }

            setLoading(true);

            const token = await currentUser.getIdToken();

            const res = await fetchWithRetry(CF_BASES.coinCheckout, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ packId: selectedOption.id }),
            });

            const data = await res.json();

            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Failed to start checkout session.');
            }
        } catch (err) {
            console.error('Checkout error:', err);
            alert(`Failed to start checkout: ${err.message || err}`);
        } finally {
            setLoading(false);
            setBuyDialogOpen(false);
            setSelectedOption(null);
        }
    };

    const handleCancel = () => {
        setBuyDialogOpen(false);
        setSelectedOption(null);
    };

    // ⭐ On mount, check URL params for status=success/cancel
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const status = params.get('status');
            const sessionId = params.get('session_id');

            if (status === 'success') {
                setSnackbarSeverity('success');
                setSnackbarMessage('Payment successful! Your coins should be arriving shortly.');
                setSnackbarOpen(true);
            } else if (status === 'cancel') {
                setSnackbarSeverity('info');
                setSnackbarMessage('Payment canceled.');
                setSnackbarOpen(true);
            }

            if (status) {
                params.delete('status');
                params.delete('session_id');
                const newQs = params.toString();
                const newUrl = `${window.location.pathname}${newQs ? `?${newQs}` : ''}`;
                window.history.replaceState(null, '', newUrl);
            }
        } catch (e) {
            console.error('Failed to parse shop URL params', e);
        }
    }, []);

    if (!userData) return <Typography sx={{ color: THEME.text }}>Loading...</Typography>;

    // ---------- Ticket helpers ----------
    const getTicketCount = (ticketType) => {
        if (!ticketType) return 0;
        const raw = userData?.drawTickets?.[ticketType];
        return typeof raw === 'number' ? raw : 0;
    };

    const getTicketsRequiredForPack = (pack) => {
        if (!pack.ticketType) return 0;
        if (typeof pack.ticketsRequired === 'number') return pack.ticketsRequired;
        return pack.isSingle ? 1 : 10;
    };

    const hasTicketsForPack = (pack) => {
        if (!pack.ticketType) return true;
        const count = getTicketCount(pack.ticketType);
        const required = getTicketsRequiredForPack(pack);
        return count >= required;
    };

    const consumeTicketsForPack = (pack) => {
        if (!pack.ticketType) return;
        const required = getTicketsRequiredForPack(pack);
        const ticketType = pack.ticketType;

        setUserData((prev) => {
            if (!prev) return prev;
            const prevCount = prev.drawTickets?.[ticketType] ?? 0;
            const newCount = Math.max(0, prevCount - required);

            const updatedDrawTickets = {
                ...(prev.drawTickets || {}),
                [ticketType]: newCount,
            };

            const next = { ...prev, drawTickets: updatedDrawTickets };

            persistUserData({ drawTickets: updatedDrawTickets });

            return next;
        });
    };

    const handleDrawPack = async (pack) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        const { type, cost, isSingle, ticketType } = pack;

        // 🔑 Payment / ticket logic
        if (type === 'single') {
            if (hasBasicTicket()) {
                consumeBasicTicket();
            } else {
                if (!spendCoins(cost)) {
                    alert('Not enough coins or tickets!');
                    inFlightRef.current = false;
                    return;
                }
            }
        } else if (ticketType) {
            if (!hasTicketsForPack(pack)) {
                const needed = getTicketsRequiredForPack(pack);
                alert(`You need at least ${needed} ${labelForTicket(ticketType)}${needed > 1 ? 's' : ''} to draw this pack.`);
                inFlightRef.current = false;
                return;
            }
            consumeTicketsForPack(pack);
        } else {
            if (!spendCoins(cost)) {
                alert('Not enough coins!');
                inFlightRef.current = false;
                return;
            }
        }

        setLoading(true);
        setDrawQueue([]);
        setCurrentDraw(null);
        setFinalDrawnCards([]);
        setDialogOpen(true);
        setFlipPhase('back');

        try {
            const count = isSingle ? 1 : 10;
            const url = `${CF_BASES.drawPack}/?type=${encodeURIComponent(type)}&count=${count}`;

            const res = await fetchWithRetry(url, { method: 'GET' }, 2);

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const text = await res.text();
                throw new Error(`Unexpected response (not JSON). Body: ${text?.slice(0, 300)}`);
            }

            const payload = await res.json();
            const cards = Array.isArray(payload) ? payload : [payload];

            addCardsToOwned(cards);

            const soulDustAmount = isSingle ? 5 : 50;
            addSoulDust(soulDustAmount);

            const animationSequence = [{ _type: 'soulDust', amount: soulDustAmount }, ...cards];

            // 🔥 Preload card art URLs & warm browser cache BEFORE starting flips
            try {
                await preloadArtForCards(cards, globalCards, setPreloadedImages);
            } catch (e) {
                console.warn('Bulk art preload failed, continuing with animation:', e);
            }

            setDrawQueue(animationSequence);
            await playDrawAnimation(animationSequence);
        } catch (err) {
            console.error('Failed to draw cards:', err);
            alert(`Draw failed: ${err?.message || err}`);
        } finally {
            setLoading(false);
            inFlightRef.current = false;
        }
    };

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const playDrawAnimation = async (items) => {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            setCurrentDraw(item);
            setFlipPhase('back');
            setFlipKey((k) => k + 1);
            await wait(320);

            setFlipPhase('front');
            setFlipKey((k) => k + 1);
            await wait(2500);

            if (!item._type || item._type !== 'soulDust') {
                setFinalDrawnCards((prev) => [...prev, item]);
            }

            await wait(200);
        }
        setCurrentDraw(null);
        setDialogOpen(false);
    };

    // Helper to look up preloaded image for any card object
    const getPreloadedImageForCard = (card) => {
        const key = getCardImageKey(card);
        if (!key) return undefined;
        return preloadedImages[key];
    };

    // ---------- Soul Dust Shop Logic ----------
    const SOUL_PACKS = [
        { soul: 100, costCoins: 100 },
        { soul: 575, costCoins: 500 },
        { soul: 1250, costCoins: 1000 },
        { soul: 3375, costCoins: 2500 },
        { soul: 10150, costCoins: 7000 },
        { soul: 15000, costCoins: 10000 },
    ];

    const handleBuySoul = ({ soul, costCoins }) => {
        if (!spendCoins(costCoins)) {
            alert('Not enough coins!');
            return;
        }
        addSoulDust(soul);
    };

    // Derived flags for current reveal
    const isSoulDustReveal = currentDraw && currentDraw._type === 'soulDust';
    const currentSoulDustAmount = isSoulDustReveal ? currentDraw.amount : 0;
    const dialogGlowColor = isSoulDustReveal
        ? THEME.gold
        : currentDraw
            ? rarityColor(currentDraw.rarity)
            : '#ccc';

    return (
        <Box
            sx={{
                background: `radial-gradient(1200px 600px at 50% -10%, rgba(212,175,55,0.07), transparent),
                     linear-gradient(180deg, #000, ${THEME.bg})`,
                color: THEME.text,
                p: { xs: 2, md: 3 },
                borderRadius: 2,
            }}
        >

            {/* Coin store */}
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Grid container spacing={3}>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_100" coins={100} price={0.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_550" coins={550} price={4.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_1200" coins={1200} price={9.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_2500" coins={2500} price={19.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_7000" coins={7000} price={49.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Paper elevation={0} sx={coinBoxStyle}>
                            <CoinBox id="coins_15000" coins={15000} price={99.99} onBuy={handleClickBuy} />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Soul Dust store */}
            <Box sx={{ px: 4, pb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                    variant="h6"
                    sx={{
                        mt: 2,
                        mb: 1,
                        fontFamily: "'Cinzel', serif",
                        color: THEME.text,
                        letterSpacing: '0.8px',
                    }}
                >
                    Soul Dust Shop
                </Typography>

                <Grid container spacing={3}>
                    {SOUL_PACKS.slice(0, 3).map((p, idx) => (
                        <Grid item xs={6} md={4} key={`soul-top-${idx}`}>
                            <Paper elevation={0} sx={soulBoxStyle}>
                                <SoulBox
                                    soul={p.soul}
                                    costCoins={p.costCoins}
                                    onBuy={handleBuySoul}
                                    disabled={loading || (userData.coins ?? 0) < p.costCoins}
                                />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {SOUL_PACKS.slice(3, 6).map((p, idx) => (
                        <Grid item xs={6} md={4} key={`soul-bot-${idx}`}>
                            <Paper elevation={0} sx={soulBoxStyle}>
                                <SoulBox
                                    soul={p.soul}
                                    costCoins={p.costCoins}
                                    onBuy={handleBuySoul}
                                    disabled={loading || (userData.coins ?? 0) < p.costCoins}
                                />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Confirm Buy Dialog */}
            <Dialog
                open={buyDialogOpen}
                onClose={handleCancel}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        background: THEME.panel,
                        color: THEME.text,
                        border: `1px solid ${THEME.borderGold}`,
                        boxShadow: THEME.shadowGold,
                    },
                }}
            >
                <DialogTitle sx={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.6px' }}>
                    Confirm Purchase
                </DialogTitle>
                <DialogContent>
                    {selectedOption && (
                        <>
                            <Typography sx={{ color: THEME.text }}>
                                Buy <strong>{selectedOption.coins.toLocaleString()} coins</strong> for{' '}
                                <strong>${selectedOption.price.toFixed(2)}</strong>?
                            </Typography>
                            <Typography sx={{ color: THEME.text, fontSize: '0.9rem', mt: 2 }}>
                                This will redirect you to Stripe to complete your purchase.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} sx={{ color: THEME.text, borderColor: THEME.goldDim }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" sx={goldButton} disabled={loading}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Packs */}
            <Box
                sx={{
                    mt: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontFamily: "'Cinzel', serif", color: THEME.text, letterSpacing: '0.8px' }}
                >
                    Available Packs
                </Typography>

                <Grid container direction="column" spacing={4} alignItems="center">
                    {PACKS
                        .filter((pack) => !pack.ticketType || hasTicketsForPack(pack))
                        .map((pack) => {
                            const { id, label, isSingle, type, ticketType } = pack;

                            const ticketsRequired = getTicketsRequiredForPack(pack);
                            const isTicketPack = !!ticketType;
                            const userCoins = userData.coins ?? 0;

                            const hasEnoughTickets = isTicketPack ? hasTicketsForPack(pack) : true;

                            const disabled =
                                loading ||
                                (type === 'single' &&
                                    !hasBasicTicket() &&
                                    userCoins < pack.cost) ||
                                (isTicketPack && !hasEnoughTickets) ||
                                (!isTicketPack &&
                                    type !== 'single' &&
                                    userCoins < pack.cost);

                            let buttonText;
                            if (type === 'single') {
                                buttonText = hasBasicTicket() ? '1 Ticket' : `${pack.cost} coins`;
                            } else if (isTicketPack) {
                                const ticketLabel = labelForTicket(ticketType);
                                buttonText = `${ticketsRequired} ${ticketLabel}${ticketsRequired > 1 ? 's' : ''}`;
                            } else {
                                buttonText = `${pack.cost} coins`;
                            }

                            return (
                                <Grid item key={id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            width: 220,
                                            height: 360,
                                            borderRadius: 10,
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            textAlign: 'center',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            border: `1px solid ${THEME.borderGold}`,
                                            boxShadow: THEME.shadowGold,
                                            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url(${cardBack})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            transition: 'box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
                                            '&:hover': {
                                                boxShadow: '0 0 22px rgba(212,175,55,0.35)',
                                                borderColor: THEME.gold,
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                color: THEME.textDim,
                                                fontWeight: 600,
                                                fontFamily: "'Cinzel', serif",
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            {isSingle ? '1 Card' : '10 Cards'}
                                        </Typography>

                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mt: 2,
                                                color: THEME.text,
                                                fontFamily: "'Cinzel', serif",
                                                letterSpacing: '0.6px',
                                                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                                            }}
                                        >
                                            {label}
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            disabled={disabled}
                                            onClick={() => handleDrawPack(pack)}
                                            sx={{ mt: 1, ...goldButton, width: '100%' }}
                                        >
                                            {buttonText}
                                        </Button>
                                    </Paper>
                                </Grid>
                            );
                        })}
                </Grid>
            </Box>

            {loading && (
                <Box sx={{ mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Flip Animation Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        overflow: 'hidden',
                        borderRadius: 3,
                        '--glow': dialogGlowColor,
                        border: `1px solid ${THEME.borderGold}`,
                        background: THEME.panel,
                        color: THEME.text,
                        animation: `${pulse} 1.8s ease-in-out infinite`,
                    },
                }}
            >
                {flipPhase === 'front' && currentDraw && (
                    <DialogTitle
                        component="div" // ✅ Fix for <h2><h6></h6></h2> nesting
                        sx={{
                            borderBottom: '1px solid rgba(212,175,55,0.2)',
                            textAlign: 'center',
                            color: THEME.text,
                            fontFamily: "'Cinzel', serif",
                            letterSpacing: '0.6px',
                        }}
                    >
                        {isSoulDustReveal ? (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: THEME.text }}>
                                    Soul Dust Reward
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    You gained{' '}
                                    <strong>{currentSoulDustAmount.toLocaleString?.() ?? currentSoulDustAmount}</strong>{' '}
                                    Soul Dust
                                </Typography>
                            </>
                        ) : null}
                    </DialogTitle>
                )}

                <DialogContent
                    sx={{
                        py: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.10), rgba(0,0,0,0) 60%)',
                    }}
                >
                    {flipPhase === 'back' && (
                        <Box key={`back-${flipKey}`} sx={backFaceStyles} />
                    )}

                    {flipPhase === 'front' && currentDraw && (
                        isSoulDustReveal ? (
                            <Box key={`front-${flipKey}`} sx={soulDustFrontStyles}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 18,
                                        left: 0,
                                        right: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '999px',
                                            background:
                                                'linear-gradient(90deg, rgba(0,0,0,0.78), rgba(0,0,0,0.9))',
                                            border: `1px solid ${THEME.goldDim}`,
                                            color: THEME.text,
                                            fontFamily: "'Cinzel', serif",
                                            letterSpacing: '0.8px',
                                        }}
                                    >
                                        +{currentSoulDustAmount.toLocaleString?.() ?? currentSoulDustAmount} Soul Dust
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <FlipCard key={`front-${flipKey}`} sx={{ maxWidth: 420, mx: 'auto' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                    <PlayingCard
                                        card={currentDraw}
                                        imageSrc={getPreloadedImageForCard(currentDraw)}
                                    />
                                </CardContent>
                            </FlipCard>
                        )
                    )}
                </DialogContent>
            </Dialog>

            {/* Final Draw Summary */}
            {finalDrawnCards.length > 0 && !dialogOpen && (
                <Box sx={{ mt: 5 }}>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.8px', color: THEME.text }}
                    >
                        Your Drawn Cards
                    </Typography>
                    <Grid container spacing={2}>
                        {finalDrawnCards.map((card, index) => (
                            <Grid item xs={12} sm={6} md={3} key={`${card.id || card.name}-${index}`}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 3,
                                        background: THEME.panel,
                                        color: THEME.text,
                                        border: `1px solid ${THEME.borderGold}`,
                                        boxShadow: THEME.shadowGold,
                                    }}
                                >
                                    <CardContent sx={{ pb: 1.5 }}>
                                        <PlayingCard
                                            card={card}
                                            imageSrc={getPreloadedImageForCard(card)}
                                        />
                                        <Box sx={{ mt: 1.5 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.text }}>
                                                {card.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: THEME.textDim }}>
                                                {card.rarity} • {classificationLabel(card.classification)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, color: THEME.text }}>
                                                <strong>ATK:</strong> {card.baseStats?.attack ?? '—'} &nbsp;|&nbsp;
                                                <strong>DEF:</strong> {card.baseStats?.defense ?? '—'}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Snackbar for payment status */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{
                        background: 'linear-gradient(180deg, rgba(212,175,55,0.18), rgba(0,0,0,0.9))',
                        color: THEME.text,
                        border: `1px solid ${THEME.borderGold}`,
                        boxShadow: THEME.shadowGold,
                        fontFamily: "'Cinzel', serif",
                        letterSpacing: '0.5px',
                        '& .MuiAlert-icon': {
                            color: THEME.gold,
                        },
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Shop;
